import {
  updateMemberProfile,
  type EducationStatus,
  type MemberProfileSnapshot,
  type PaymentPreference,
} from '@mtc/shared';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Field } from '../components/Field';
import { ImgPlaceholder } from '../components/ImgPlaceholder';
import { StateMessage } from '../components/StateMessage';
import { useFormations, useSubmitInscription } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../lib/auth';
import { gaEvent, gaPageView } from '../lib/analytics';
import { fbqTrack } from '../lib/metaPixel';
import { trackEvent, trackInscriptionStep } from '../lib/track';
import type { FormationSession } from '../types';

/** Dedicated thank-you URL a completed inscription lands on — a stable,
 *  linkable page for ad conversion tracking (Google Ads / Meta / GA4). No
 *  personal data is ever put in this URL. */
const MERCI_PATH = '/inscription/merci';

/** A session can be registered for only if it's open AND still has seats.
 *  `registrationOpen` already encodes "status === open && seats remain", but we
 *  double-check `placesRestantes` defensively. Soldout/custom sessions are out. */
const isSessionRegistrable = (s: FormationSession): boolean =>
  s.registrationOpen && s.placesRestantes > 0;

// The form is navigated as an ordered list of "stages". The `select` stage is
// dropped entirely when the visitor arrives from a formation page with a valid
// formation+session already in the URL — they land straight on the form instead
// of re-picking what they already chose (the old redundant "Session" step).
// Identité + Études are merged into a single `infos` stage to cut clicks.
type Stage = 'select' | 'infos' | 'paiement';

const STAGE_LABELS: Record<Stage, string> = {
  select: 'Formation',
  infos: 'Vos informations',
  paiement: 'Paiement & validation',
};

const EDUCATION_OPTIONS: { value: EducationStatus; label: string; hint: string }[] = [
  { value: 'dentiste', label: 'Dentiste', hint: 'En exercice depuis plus de 3 ans' },
  { value: 'jeune-diplome', label: 'Jeune diplômé·e', hint: 'Diplôme obtenu il y a moins de 3 ans' },
  {
    value: 'etudiant-these',
    label: 'Étudiant·e en instance de thèse',
    hint: '6ème année / soutenance prévue',
  },
];

// Installments (`installments-2` / `installments-3`) are still valid values in
// `paymentPreferenceSchema` so historical inscriptions parse cleanly, but the
// public form only offers single-payment methods. Échelonné is now negotiated
// directly with the admin after a candidate signs up.
const PAYMENT_OPTIONS: { value: PaymentPreference; label: string; hint: string }[] = [
  { value: 'virement', label: 'Par virement', hint: 'Paiement complet par virement bancaire' },
  { value: 'cheque', label: 'Par chèque', hint: 'Paiement complet par chèque' },
  { value: 'espece', label: 'En espèces', hint: 'Paiement complet en espèces' },
];

type FormState = {
  formation: string;
  /** Selected session id within the formation. Reset when formation changes. */
  sessionId: string;
  prenom: string;
  nom: string;
  dateNaissance: string;
  nationalite: string;
  email: string;
  telephone: string;
  etudes: EducationStatus | '';
  anneesExercice: string;
  anneeDoctorat: string;
  faculte: string;
  autresDiplomes: string;
  cabinet: string;
  villeExercice: string;
  paiement: PaymentPreference | '';
  cgv: boolean;
  newsletter: boolean;
};

const EMPTY: FormState = {
  formation: '',
  sessionId: '',
  prenom: '',
  nom: '',
  dateNaissance: '',
  nationalite: 'Marocaine',
  email: '',
  telephone: '',
  etudes: '',
  anneesExercice: '',
  anneeDoctorat: '',
  faculte: '',
  autresDiplomes: '',
  cabinet: '',
  villeExercice: '',
  paiement: '',
  cgv: false,
  newsletter: false,
};

export function Inscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialFormation = searchParams.get('formation');
  const initialSession = searchParams.get('session');
  const formationsQ = useFormations();
  const submitMutation = useSubmitInscription();
  const { user, member, isVerified, refresh: refreshAuth, signUp } = useAuth();

  // Stage-based navigation. `stageIdx` stays null until formations load so the
  // stage list is computed from real data; `done` flips to the confirmation view
  // after a successful submit.
  const [stageIdx, setStageIdx] = useState<number | null>(null);
  const [done, setDone] = useState(false);
  // Whether the visitor arrived pre-selected — FROZEN once formations first load.
  // Deriving it live every render would let a background refetch reshape the
  // stage list mid-flow and silently jump the user to a different stage/édition.
  // null = not yet decided.
  const [preSelectFrozen, setPreSelectFrozen] = useState<boolean | null>(null);
  // Tracks whether the form was auto-populated from the signed-in member's
  // profile snapshot. Toggles the "Vos infos pré-remplies" banner on the infos
  // step and lets the member clear the fields if they're signing someone else up.
  const [prefilledFromProfile, setPrefilledFromProfile] = useState(false);
  const [form, setForm] = useState<FormState>({
    ...EMPTY,
    formation: initialFormation ?? '',
    sessionId: initialSession ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [reference, setReference] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Optional account creation, shown on the confirmation screen.
  const [accountPassword, setAccountPassword] = useState('');
  const [accountConfirm, setAccountConfirm] = useState('');
  const [accountState, setAccountState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [accountError, setAccountError] = useState<string | null>(null);

  // Did the visitor arrive with a valid formation+session pair in the URL (i.e.
  // they clicked "S'inscrire" on a specific édition)? If so, the `select` stage
  // is skipped so they don't re-pick what they already chose.
  const camePreSelected = preSelectFrozen === true;
  const stages: Stage[] = camePreSelected ? ['infos', 'paiement'] : ['select', 'infos', 'paiement'];

  // Pre-fill from member profile snapshot when the user is already signed in.
  // Also pulls educationStatus + yearsOfPractice (added to the snapshot in
  // packages/shared) so the entire Identité + Études fields come pre-filled.
  useEffect(() => {
    if (!user || !member?.profile) return;
    const p = member.profile;
    setForm((prev) => ({
      ...prev,
      prenom: prev.prenom || p.firstName || '',
      nom: prev.nom || p.lastName || '',
      email: prev.email || user.email || '',
      telephone: prev.telephone || p.phone || '',
      dateNaissance: prev.dateNaissance || p.birthdate || '',
      nationalite: prev.nationalite || p.nationality || 'Marocaine',
      cabinet: prev.cabinet || p.cabinet || '',
      villeExercice: prev.villeExercice || p.cityOfPractice || '',
      etudes: prev.etudes || p.educationStatus || '',
      anneesExercice:
        prev.anneesExercice ||
        (typeof p.yearsOfPractice === 'number' ? String(p.yearsOfPractice) : ''),
      anneeDoctorat:
        prev.anneeDoctorat ||
        (typeof p.graduationYear === 'number' ? String(p.graduationYear) : ''),
      faculte: prev.faculte || p.faculty || '',
      autresDiplomes: prev.autresDiplomes || p.otherCredentials || '',
    }));
    setPrefilledFromProfile(true);
    // Intentional: only run when member identity changes, not on every form keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, member?.uid]);

  // Once formations first load: freeze the pre-selection decision and start at
  // stage 0. The `select` stage is skipped ONLY when the URL points at a session
  // that is actually registrable (open + seats left) — a deep-link/bookmark to a
  // now-full or soldout édition falls back to the select stage so its disabled
  // radio + validation block the signup instead of silently sailing through.
  useEffect(() => {
    if (preSelectFrozen !== null) return;
    if (!formationsQ.data) return;
    const ok = Boolean(
      initialFormation &&
        initialSession &&
        formationsQ.data.some(
          (x) =>
            x.id === initialFormation &&
            x.upcomingSessions.some(
              (s) => s.id === initialSession && isSessionRegistrable(s),
            ),
        ),
    );
    setPreSelectFrozen(ok);
    setStageIdx(0);
  }, [formationsQ.data, preSelectFrozen, initialFormation, initialSession]);

  // GA4 funnel — emit a step event whenever the visitor reaches a new stage.
  useEffect(() => {
    if (stageIdx === null) return;
    const st = stages[stageIdx];
    if (!st) return;
    trackInscriptionStep(stageIdx, st);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageIdx, camePreSelected]);

  useEffect(() => {
    if (done) trackInscriptionStep(stages.length, 'confirmation');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  // "Vider les champs" — wipe the identity/études/payment fields so a member
  // can sign up someone else (e.g. an assistant). Formation/session are
  // preserved. The banner stays dismissed for the rest of this visit.
  const clearPrefilled = () => {
    setForm((p) => ({
      ...EMPTY,
      formation: p.formation,
      sessionId: p.sessionId,
    }));
    setPrefilledFromProfile(false);
  };

  useSEO({
    title: "S'inscrire à une formation",
    description: 'Inscription en ligne aux formations MTC — Moroccan Training Center.',
  });

  // Default to the first REGISTRABLE formation (one with an upcoming édition).
  // Also snaps away if the current selection (e.g. a deep-linked past-only
  // formation) has no upcoming édition, so the user is never stuck.
  useEffect(() => {
    const list = formationsQ.data ?? [];
    const registrable = list.filter((x) => x.upcomingSessions.length > 0);
    if (registrable.length === 0) return;
    const current = registrable.find((x) => x.id === form.formation);
    if (!current) {
      const first = registrable[0]!;
      setForm((p) => ({
        ...p,
        formation: first.id,
        sessionId: first.upcomingSessions[0]?.id ?? '',
      }));
    }
  }, [formationsQ.data, form.formation]);

  // When the formation changes (or sessionId is invalid for it), snap sessionId
  // to the first upcoming session of the selected formation.
  useEffect(() => {
    const formation = formationsQ.data?.find((x) => x.id === form.formation);
    if (!formation) return;
    const valid = formation.upcomingSessions.some((s) => s.id === form.sessionId);
    if (!valid && formation.upcomingSessions.length > 0) {
      setForm((p) => ({ ...p, sessionId: formation.upcomingSessions[0]?.id ?? '' }));
    }
  }, [form.formation, form.sessionId, formationsQ.data]);

  // The thank-you URL only exists as a landing spot right after a completed
  // submission (the confirmation is held in `done`/`reference` state, set via
  // replaceState — React-Router never actually routes here in that case). A
  // direct hit / refresh of /inscription/merci has no completion to show, so
  // send them back to the form.
  if (location.pathname === MERCI_PATH && !done) return <Navigate to="/inscription" replace />;

  if (formationsQ.isLoading) return <StateMessage variant="loading" />;
  if (formationsQ.error) return <StateMessage variant="error" message={String(formationsQ.error)} />;
  // Hold the first paint until the pre-selection decision is frozen, so a
  // pre-selected visitor never flashes the (about-to-be-skipped) select stage.
  if (preSelectFrozen === null) return <StateMessage variant="loading" />;

  const formations = formationsQ.data ?? [];
  // Only formations with at least one upcoming édition can be registered for.
  const registrable = formations.filter((x) => x.upcomingSessions.length > 0);
  const f = formations.find((x) => x.id === form.formation) ?? registrable[0];
  if (!f) {
    return (
      <StateMessage
        variant="empty"
        message="Aucune formation avec une édition à venir pour le moment. Contactez-nous via WhatsApp pour les prochaines dates."
      />
    );
  }
  const selectedSession =
    f.upcomingSessions.find((s) => s.id === form.sessionId) ?? f.upcomingSessions[0];

  const currentStage: Stage = stages[stageIdx ?? 0] ?? stages[0]!;
  const stagePos = stageIdx ?? 0;

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const validateStage = (st: Stage): boolean => {
    const e: Record<string, string> = {};
    if (st === 'select') {
      if (!form.formation) e.formation = 'Choisissez une formation';
      else if (!form.sessionId) e.sessionId = 'Choisissez une date';
      else {
        // Block advancing with a full/soldout édition (e.g. a stale deep-link
        // that pre-checked a now-unavailable session).
        const sel = f.upcomingSessions.find((s) => s.id === form.sessionId);
        if (!sel || !isSessionRegistrable(sel))
          e.sessionId = "Cette édition n'est plus disponible — choisissez-en une autre.";
      }
    }
    if (st === 'infos') {
      // Identité
      if (!form.prenom.trim()) e.prenom = 'Requis';
      if (!form.nom.trim()) e.nom = 'Requis';
      if (!form.dateNaissance || !/^\d{4}-\d{2}-\d{2}$/.test(form.dateNaissance))
        e.dateNaissance = 'Date invalide';
      if (!form.nationalite.trim()) e.nationalite = 'Requis';
      if (!form.email || !/.+@.+\..+/.test(form.email)) e.email = 'Email invalide';
      if (!form.telephone || form.telephone.replace(/\D/g, '').length < 8)
        e.telephone = 'Téléphone invalide';
      // Études & exercice
      if (!form.etudes) e.etudes = 'Indiquez votre statut';
      if (!form.villeExercice.trim()) e.villeExercice = 'Requis';
      if (
        form.anneesExercice &&
        (!/^\d+$/.test(form.anneesExercice) || Number(form.anneesExercice) > 70)
      )
        e.anneesExercice = 'Nombre entier entre 0 et 70';
    }
    if (st === 'paiement') {
      // Payment preference is optional — it's negotiated during the 48h contact
      // and there's no online payment, so requiring it upfront only adds friction.
      if (!form.cgv) e.cgv = 'Vous devez accepter les CGV';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = async () => {
    if (!validateStage(currentStage)) return;
    if (currentStage === 'paiement') {
      const sessionId = form.sessionId || f.sessionId;
      if (!sessionId) {
        setSubmitError("Cette formation n'a pas encore d'édition programmée.");
        return;
      }
      setSubmitError(null);
      try {
        // Only attach memberUid when the signed-in user is verified AND
        // they're submitting under their own email (otherwise the rule
        // rejects it).
        const memberUid =
          isVerified && user?.email?.toLowerCase() === form.email.trim().toLowerCase()
            ? user.uid
            : null;
        const id = await submitMutation.mutateAsync({
          formationId: f.id,
          sessionId,
          profile: {
            firstName: form.prenom.trim(),
            lastName: form.nom.trim(),
            email: form.email.trim(),
            phone: form.telephone.trim(),
            birthdate: form.dateNaissance,
            nationality: form.nationalite.trim(),
            educationStatus: form.etudes as EducationStatus,
            ...(form.anneesExercice ? { yearsOfPractice: Number(form.anneesExercice) } : {}),
            ...(form.cabinet.trim() ? { cabinet: form.cabinet.trim() } : {}),
            cityOfPractice: form.villeExercice.trim(),
            // Academic background — year + faculty are skipped for students
            // (the inputs are hidden for them); other credentials apply to all.
            ...(form.anneeDoctorat && form.etudes !== 'etudiant-these'
              ? { graduationYear: Number(form.anneeDoctorat) }
              : {}),
            ...(form.faculte.trim() && form.etudes !== 'etudiant-these'
              ? { faculty: form.faculte.trim() }
              : {}),
            ...(form.autresDiplomes.trim()
              ? { otherCredentials: form.autresDiplomes.trim() }
              : {}),
            // Optional — only attach when the candidate actually picked one.
            ...(form.paiement ? { paymentPreference: form.paiement as PaymentPreference } : {}),
            newsletter: form.newsletter,
          },
          memberUid,
        });

        // Save the latest profile back to the member doc so the next signup
        // is auto-filled. Best-effort — we never block the success screen on
        // a profile write failure. Only when (a) the user is signed in and
        // (b) the submission email matches their auth email (otherwise we'd
        // be overwriting their profile with someone else's data).
        if (memberUid && user) {
          const snapshot: MemberProfileSnapshot = {
            firstName: form.prenom.trim(),
            lastName: form.nom.trim(),
            phone: form.telephone.trim(),
            ...(form.dateNaissance ? { birthdate: form.dateNaissance } : {}),
            ...(form.nationalite.trim() ? { nationality: form.nationalite.trim() } : {}),
            ...(form.cabinet.trim() ? { cabinet: form.cabinet.trim() } : {}),
            ...(form.villeExercice.trim()
              ? { cityOfPractice: form.villeExercice.trim() }
              : {}),
            ...(form.etudes ? { educationStatus: form.etudes as EducationStatus } : {}),
            ...(form.anneesExercice
              ? { yearsOfPractice: Number(form.anneesExercice) }
              : {}),
            ...(form.anneeDoctorat && form.etudes !== 'etudiant-these'
              ? { graduationYear: Number(form.anneeDoctorat) }
              : {}),
            ...(form.faculte.trim() && form.etudes !== 'etudiant-these'
              ? { faculty: form.faculte.trim() }
              : {}),
            ...(form.autresDiplomes.trim()
              ? { otherCredentials: form.autresDiplomes.trim() }
              : {}),
          };
          updateMemberProfile(user.uid, snapshot)
            .then(() => refreshAuth())
            .catch((err) => {
              // Don't surface this to the user — they've successfully signed up.
              console.warn('updateMemberProfile after inscription failed:', err);
            });
        }

        trackEvent('inscription_complete');
        setReference(id);
        setDone(true);

        // Land on the dedicated thank-you URL (for ad conversion tracking).
        // replaceState keeps the confirmation screen mounted (no remount / no
        // PII in the URL); React-Router's own location stays on /inscription.
        try {
          window.history.replaceState(null, '', MERCI_PATH);
        } catch {
          /* ignore — history API unavailable */
        }
        // Conversion signals fired only on a genuine completion:
        gaPageView(MERCI_PATH);
        gaEvent('generate_lead', {
          currency: f.devise,
          value: f.prix,
          formation_id: f.id,
          transaction_id: id,
        });
        // Meta Pixel conversion — standard 'Lead' event on a completed inscription.
        fbqTrack('Lead', {
          content_name: f.titre,
          content_ids: [f.id],
          currency: f.devise,
          value: f.prix,
        });
      } catch (err) {
        setSubmitError(
          err instanceof Error ? err.message.replace(/^Firebase: /, '') : String(err),
        );
      }
      return;
    }
    setStageIdx((i) => Math.min(stages.length - 1, (i ?? 0) + 1));
  };
  const prev = () => setStageIdx((i) => Math.max(0, (i ?? 0) - 1));

  return (
    <div className="page-inscription">
      <button type="button" className="mtc-back" onClick={() => navigate('/formations')}>
        ← Retour
      </button>
      <div className="ins-head">
        <span className="mtc-eyebrow">Inscription</span>
        <h1>{done ? 'Inscription reçue' : "S'inscrire à une formation"}</h1>
      </div>

      {!done && (
        <div className="ins-stepper">
          {stages.map((s, i) => (
            <div
              key={s}
              className={`ins-step ${i === stagePos ? 'is-on' : ''} ${i < stagePos ? 'is-done' : ''}`}
            >
              <div className="ins-step-num">{i < stagePos ? '✓' : i + 1}</div>
              <div className="ins-step-label">{STAGE_LABELS[s]}</div>
            </div>
          ))}
        </div>
      )}

      <div className="ins-grid">
        <div className="ins-form">
          {/* Pre-selected recap — shown when the visitor arrived from a formation
              page. Confirms what they're signing up for without a full step, and
              offers a way back to change it. */}
          {!done && camePreSelected && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
                padding: '12px 16px',
                background: 'rgba(11, 93, 59, 0.06)',
                border: '0.5px solid rgba(11, 93, 59, 0.18)',
                borderRadius: 10,
                marginBottom: 24,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <span
                  style={{
                    fontFamily: 'var(--mtc-font-mono)',
                    fontSize: 10,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'rgba(15,25,22,0.55)',
                    display: 'block',
                    marginBottom: 3,
                  }}
                >
                  Vous vous inscrivez à
                </span>
                <strong style={{ color: 'var(--mtc-primary)' }}>{f.titre}</strong>
                {selectedSession && (
                  <span style={{ color: 'rgba(15,25,22,0.7)' }}>
                    {' '}
                    · {selectedSession.nom} · {selectedSession.dates}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => navigate(`/formations/${f.id}`)}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  color: 'var(--mtc-primary)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  font: 'inherit',
                  flexShrink: 0,
                }}
              >
                Changer
              </button>
            </div>
          )}

          {!done && currentStage === 'select' && (
            <div className="ins-section">
              <h2>Quelle formation ?</h2>
              <p className="ins-help">Sélectionnez la formation qui vous intéresse.</p>
              <div className="ins-formations">
                {registrable.map((x) => (
                  <label
                    key={x.id}
                    className={`ins-formation-card ${form.formation === x.id ? 'is-on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="formation"
                      checked={form.formation === x.id}
                      onChange={() => update('formation', x.id)}
                    />
                    <div className="ins-formation-card-body">
                      <div className="ins-formation-card-domain">{x.domaine}</div>
                      <h4>{x.titre}</h4>
                      <div className="ins-formation-card-meta">
                        {x.upcomingSessions.length} édition
                        {x.upcomingSessions.length > 1 ? 's' : ''} à venir ·{' '}
                        {x.prixParModule.toLocaleString('fr-FR')} {x.devise} HT / module
                      </div>
                    </div>
                    <div className="ins-formation-card-radio" />
                  </label>
                ))}
              </div>
              {errors.formation && <p className="ins-error">{errors.formation}</p>}

              {f.upcomingSessions.length > 0 && (
                <>
                  <h3 style={{ marginTop: 32 }}>Quelle édition ?</h3>
                  <p className="ins-help">
                    {f.upcomingSessions.length === 1
                      ? 'Une seule édition est actuellement programmée.'
                      : 'Plusieurs éditions sont programmées — choisissez celle qui vous convient.'}
                  </p>
                  <div className="ins-radio-grid">
                    {f.upcomingSessions.map((s) => {
                      const checked = form.sessionId === s.id;
                      const full = s.placesRestantes === 0;
                      const soldout = s.registrationStatus === 'soldout';
                      const custom = s.registrationStatus === 'custom';
                      const disabled = full || soldout || custom;
                      return (
                        <label
                          key={s.id}
                          className={`ins-radio-card ${checked ? 'is-on' : ''}`}
                          style={
                            disabled
                              ? { opacity: 0.5, cursor: 'not-allowed' }
                              : undefined
                          }
                        >
                          <input
                            type="radio"
                            name="session"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => update('sessionId', s.id)}
                          />
                          <div>
                            <strong style={{ display: 'block' }}>{s.nom}</strong>
                            <span
                              style={{
                                color: 'rgba(15,25,22,0.6)',
                                fontSize: 12,
                                display: 'block',
                                marginTop: 2,
                              }}
                            >
                              {s.dates}
                            </span>
                            <span
                              style={{
                                color: 'rgba(15,25,22,0.55)',
                                fontSize: 12,
                              }}
                            >
                              {s.lieu}
                              {soldout || full
                                ? ' · Complète'
                                : custom
                                  ? ` · ${s.customStatus || 'Inscriptions fermées'}`
                                  : ' · Inscriptions ouvertes'}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {errors.sessionId && <p className="ins-error">{errors.sessionId}</p>}
                </>
              )}
            </div>
          )}

          {!done && currentStage === 'infos' && (
            <div className="ins-section">
              <h2>Vos informations</h2>
              {prefilledFromProfile && (
                <div
                  className="ins-prefilled-banner"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px 16px',
                    background: 'rgba(11, 93, 59, 0.06)',
                    border: '0.5px solid rgba(11, 93, 59, 0.18)',
                    borderRadius: 10,
                    marginBottom: 20,
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: 'var(--mtc-ink-2)',
                  }}
                >
                  <span style={{ flexShrink: 0, fontSize: 16, lineHeight: 1.2 }}>✓</span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: 'var(--mtc-primary)' }}>
                      Informations pré-remplies depuis votre profil.
                    </strong>{' '}
                    Modifiez ce qui doit l'être. Vous inscrivez quelqu'un d'autre ?{' '}
                    <button
                      type="button"
                      onClick={clearPrefilled}
                      style={{
                        background: 'none',
                        border: 0,
                        padding: 0,
                        color: 'var(--mtc-primary)',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        font: 'inherit',
                      }}
                    >
                      Vider les champs
                    </button>
                    .
                  </div>
                </div>
              )}

              <div className="ins-row-2">
                <Field
                  label="Prénom"
                  value={form.prenom}
                  onChange={(v) => update('prenom', v)}
                  error={errors.prenom}
                  autoComplete="given-name"
                />
                <Field
                  label="Nom"
                  value={form.nom}
                  onChange={(v) => update('nom', v)}
                  error={errors.nom}
                  autoComplete="family-name"
                />
              </div>
              <div className="ins-row-2">
                <Field
                  label="Date de naissance"
                  type="date"
                  value={form.dateNaissance}
                  onChange={(v) => update('dateNaissance', v)}
                  error={errors.dateNaissance}
                  autoComplete="bday"
                />
                <Field
                  label="Nationalité"
                  value={form.nationalite}
                  onChange={(v) => update('nationalite', v)}
                  error={errors.nationalite}
                  placeholder="Ex. Marocaine"
                  autoComplete="country-name"
                />
              </div>
              <Field
                label="Email professionnel"
                type="email"
                value={form.email}
                onChange={(v) => update('email', v)}
                error={errors.email}
                inputMode="email"
                autoComplete="email"
                placeholder="prenom.nom@exemple.com"
              />
              <Field
                label="Téléphone"
                type="tel"
                value={form.telephone}
                onChange={(v) => update('telephone', v)}
                error={errors.telephone}
                placeholder="+212 6 00 00 00 00"
                hint="Format international recommandé : +212 6 00 00 00 00"
                inputMode="tel"
                autoComplete="tel"
              />

              <h3 style={{ marginTop: 32 }}>Études & exercice</h3>
              <p className="ins-help">Aidez-nous à adapter le programme à votre profil.</p>

              <div className="ins-radio-grid">
                {EDUCATION_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`ins-radio-card ${form.etudes === opt.value ? 'is-on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="etudes"
                      checked={form.etudes === opt.value}
                      onChange={() => update('etudes', opt.value)}
                    />
                    <div>
                      <strong style={{ display: 'block', marginBottom: 2 }}>{opt.label}</strong>
                      <span style={{ color: 'rgba(15,25,22,0.55)', fontSize: 12 }}>{opt.hint}</span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.etudes && <p className="ins-error">{errors.etudes}</p>}

              <div className="ins-row-2" style={{ marginTop: 24 }}>
                <Field
                  label="Cabinet (si applicable)"
                  value={form.cabinet}
                  onChange={(v) => update('cabinet', v)}
                  placeholder="Cabinet Dr. ..."
                  autoComplete="organization"
                />
                <Field
                  label="Années d'exercice"
                  type="number"
                  value={form.anneesExercice}
                  onChange={(v) => update('anneesExercice', v)}
                  error={errors.anneesExercice}
                  placeholder="Ex. 5"
                  hint="0 si vous êtes étudiant·e"
                  inputMode="numeric"
                  min={0}
                  max={70}
                />
              </div>
              <Field
                label="Ville d'exercice"
                value={form.villeExercice}
                onChange={(v) => update('villeExercice', v)}
                error={errors.villeExercice}
                placeholder="Casablanca, Rabat…"
                autoComplete="address-level2"
              />

              {/* Academic background — mirrors the alumni sheet. Year + faculty
                  are hidden for students (they haven't graduated yet); "Autres
                  diplômes" stays for everyone. */}
              {form.etudes !== 'etudiant-these' && (
                <div className="ins-row-2" style={{ marginTop: 4 }}>
                  <Field
                    label="Année d'obtention du doctorat"
                    type="number"
                    value={form.anneeDoctorat}
                    onChange={(v) => update('anneeDoctorat', v)}
                    error={errors.anneeDoctorat}
                    placeholder="Ex. 2018"
                    inputMode="numeric"
                    min={1950}
                    max={2100}
                  />
                  <Field
                    label="Faculté d'obtention"
                    value={form.faculte}
                    onChange={(v) => update('faculte', v)}
                    placeholder="Ex. Faculté de Médecine Dentaire, Casablanca"
                  />
                </div>
              )}
              <Field
                label="Autres diplômes et certificats"
                value={form.autresDiplomes}
                onChange={(v) => update('autresDiplomes', v)}
                placeholder="Ex. Master en endodontie, CES de parodontologie…"
                hint="Optionnel — séparez par des virgules"
              />
            </div>
          )}

          {!done && currentStage === 'paiement' && (
            <div className="ins-section">
              <h2>Mode de paiement <span style={{ color: 'var(--mtc-mute)', fontWeight: 400 }}>(optionnel)</span></h2>
              <p className="ins-help">
                Facultatif — vous pourrez en discuter lors de la prise de contact. L'équipe MTC
                confirmera les modalités définitives avec vous.
              </p>

              {/* Full price breakdown — shown here (not earlier) so the per-module
                  figure leads the journey and the total appears at the payment step. */}
              <div
                style={{
                  border: '0.5px solid var(--mtc-line)',
                  borderRadius: 12,
                  padding: '16px 18px',
                  margin: '4px 0 24px',
                  background: 'rgba(11,93,59,0.03)',
                }}
              >
                <div className="ins-summary-row">
                  <span>Tarif par module</span>
                  <strong>
                    {f.prixParModule.toLocaleString('fr-FR')} {f.devise} HT
                  </strong>
                </div>
                <div className="ins-summary-row">
                  <span>Nombre de modules</span>
                  <strong>{f.programme.length}</strong>
                </div>
                <div className="ins-summary-total">
                  <span>Total de la formation</span>
                  <strong>
                    {f.prix.toLocaleString('fr-FR')} {f.devise} HT
                  </strong>
                </div>
              </div>

              <div className="ins-radio-grid">
                {PAYMENT_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`ins-radio-card ${form.paiement === opt.value ? 'is-on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="paiement"
                      checked={form.paiement === opt.value}
                      onChange={() => update('paiement', opt.value)}
                    />
                    <div>
                      <strong style={{ display: 'block', marginBottom: 2 }}>{opt.label}</strong>
                      <span style={{ color: 'rgba(15,25,22,0.55)', fontSize: 12 }}>{opt.hint}</span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.paiement && <p className="ins-error">{errors.paiement}</p>}

              <h3 style={{ marginTop: 32 }}>Validez votre demande</h3>
              <p className="ins-help">
                Un membre de l'équipe MTC vous contactera sous 48h pour confirmer votre inscription
                et organiser le paiement.
              </p>

              <div className="ins-cgv" style={{ marginTop: 16 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={form.cgv}
                    onChange={(e) => update('cgv', e.target.checked)}
                  />{' '}
                  J'accepte les{' '}
                  <button
                    type="button"
                    className="mtc-btn mtc-btn-link"
                    onClick={() => navigate('/confidentialite')}
                  >
                    conditions et la politique de confidentialité
                  </button>
                  .
                </label>
                {errors.cgv && <p className="ins-error">{errors.cgv}</p>}
                <label>
                  <input
                    type="checkbox"
                    checked={form.newsletter}
                    onChange={(e) => update('newsletter', e.target.checked)}
                  />{' '}
                  Je souhaite recevoir le programme MTC par email.
                </label>
              </div>
            </div>
          )}

          {done && (
            <div className="ins-section ins-confirm">
              <div className="ins-confirm-check">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="22" stroke="#0B5D3B" strokeWidth="1.5" />
                  <path
                    d="M14 24l7 7 14-14"
                    stroke="#0B5D3B"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2>Merci, {form.prenom} !</h2>
              <p>
                Votre demande d'inscription à <strong>{f.titre}</strong> est enregistrée. Un membre de
                l'équipe MTC vous contactera sous 48h au <strong>{form.telephone}</strong> ou par email
                à <strong>{form.email}</strong> pour confirmer et organiser le paiement.
              </p>
              <div className="ins-confirm-card">
                <div>
                  <span>Référence</span>
                  <strong>{reference ? reference.slice(0, 8).toUpperCase() : '—'}</strong>
                </div>
                <div>
                  <span>Formation</span>
                  <strong>{f.titre}</strong>
                </div>
                <div>
                  <span>Dates</span>
                  <strong>{selectedSession?.dates ?? f.dates}</strong>
                </div>
                <div>
                  <span>Lieu</span>
                  <strong>{selectedSession?.lieu ?? f.lieu}</strong>
                </div>
                <div>
                  <span>Statut</span>
                  <strong>En attente de contact</strong>
                </div>
              </div>
              <div className="ins-confirm-cta">
                <button
                  type="button"
                  className="mtc-btn mtc-btn-primary"
                  onClick={() => navigate('/formations')}
                >
                  Voir d'autres formations
                </button>
              </div>

              {/* Optional account creation — only shown if not signed in. */}
              {!user && accountState !== 'done' && (
                <div
                  style={{
                    marginTop: 32,
                    padding: '20px 24px',
                    border: '1px solid rgba(11,93,59,0.2)',
                    background: 'rgba(11,93,59,0.04)',
                    borderRadius: 12,
                  }}
                >
                  <h3 style={{ margin: '0 0 6px', fontSize: 18 }}>
                    Suivre cette inscription en ligne ?
                  </h3>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'rgba(15,25,22,0.7)',
                      lineHeight: 1.5,
                      margin: '0 0 14px',
                    }}
                  >
                    Créez un mot de passe pour <strong>{form.email}</strong> et accédez à votre
                    espace membre — paiement, certificat, inscriptions futures sans ressaisie.
                    Vous pourrez aussi passer cette étape.
                  </p>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 10,
                      maxWidth: 480,
                    }}
                  >
                    <Field
                      label="Mot de passe"
                      type="password"
                      value={accountPassword}
                      onChange={setAccountPassword}
                    />
                    <Field
                      label="Confirmer"
                      type="password"
                      value={accountConfirm}
                      onChange={setAccountConfirm}
                    />
                  </div>
                  {accountError && (
                    <p className="ins-error" style={{ marginTop: 8 }}>
                      {accountError}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <button
                      type="button"
                      className="mtc-btn mtc-btn-primary"
                      disabled={accountState === 'busy'}
                      onClick={async () => {
                        setAccountError(null);
                        if (accountPassword.length < 8) {
                          setAccountError('Mot de passe : 8 caractères minimum.');
                          return;
                        }
                        if (accountPassword !== accountConfirm) {
                          setAccountError('Les mots de passe ne correspondent pas.');
                          return;
                        }
                        setAccountState('busy');
                        try {
                          await signUp(
                            form.email,
                            accountPassword,
                            `${form.prenom.trim()} ${form.nom.trim()}`,
                          );
                          setAccountState('done');
                        } catch (err) {
                          const msg = err instanceof Error ? err.message : String(err);
                          setAccountError(
                            msg.includes('email-already-in-use')
                              ? 'Cet email a déjà un compte. Connectez-vous pour le lier.'
                              : msg.replace(/^Firebase: /, ''),
                          );
                          setAccountState('error');
                        }
                      }}
                    >
                      {accountState === 'busy' ? 'Création…' : 'Créer mon compte'}
                    </button>
                    <button
                      type="button"
                      className="mtc-btn mtc-btn-ghost"
                      onClick={() => setAccountState('done')}
                    >
                      Plus tard
                    </button>
                  </div>
                </div>
              )}

              {accountState === 'done' && !user && (
                <p
                  style={{
                    marginTop: 24,
                    fontSize: 13,
                    color: 'rgba(15,25,22,0.65)',
                  }}
                >
                  Vous pourrez créer un compte à tout moment avec cet email depuis l'espace membre.
                </p>
              )}

              {accountState === 'done' && user && (
                <div
                  style={{
                    marginTop: 24,
                    padding: '14px 18px',
                    background: 'rgba(11,93,59,0.08)',
                    border: '1px solid rgba(11,93,59,0.2)',
                    borderRadius: 10,
                    fontSize: 13,
                    color: '#0B5D3B',
                  }}
                >
                  ✓ Compte créé. Un email de vérification est en route. Une fois confirmé, votre
                  inscription apparaîtra dans <a href="/mon-espace" style={{ color: 'inherit' }}>votre espace membre</a>.
                </div>
              )}
            </div>
          )}

          {!done && (
            <>
              {submitError && (
                <p className="ins-error" role="alert">
                  {submitError}
                </p>
              )}
              <div className="ins-actions">
                {stagePos > 0 && (
                  <button type="button" className="mtc-btn mtc-btn-ghost" onClick={prev}>
                    ← Précédent
                  </button>
                )}
                <button
                  type="button"
                  className="mtc-btn mtc-btn-primary"
                  onClick={next}
                  disabled={submitMutation.isPending}
                >
                  {submitMutation.isPending
                    ? 'Envoi…'
                    : currentStage === 'paiement'
                      ? 'Envoyer ma demande'
                      : 'Continuer →'}
                </button>
              </div>
            </>
          )}
        </div>

        {!done && (
          <aside className="ins-summary">
            <div className="ins-summary-card">
              <span className="mtc-eyebrow">Récapitulatif</span>
              <h3>{f.titre}</h3>
              <ImgPlaceholder
                label={f.image}
                ratio="16/9"
                tone="vert"
                className="ins-summary-img"
                src={f.heroImage}
                alt={f.titre}
              />
              <div className="ins-summary-row">
                <span>Domaine</span>
                <strong>{f.domaine}</strong>
              </div>
              <div className="ins-summary-row">
                <span>Dates</span>
                <strong>{selectedSession?.dates ?? f.dates}</strong>
              </div>
              <div className="ins-summary-row">
                <span>Lieu</span>
                <strong>{selectedSession?.lieu ?? f.lieu}</strong>
              </div>
              <div className="ins-summary-row">
                <span>Durée</span>
                <strong>
                  {f.duree} · {f.heures}h
                </strong>
              </div>
              <div className="ins-summary-total">
                <span>Tarif par module</span>
                <strong>
                  {f.prixParModule.toLocaleString('fr-FR')} {f.devise} HT
                </strong>
              </div>
              <p
                style={{
                  marginTop: 12,
                  fontSize: 11,
                  color: 'rgba(15,25,22,0.55)',
                  lineHeight: 1.5,
                }}
              >
                Le tarif total de la formation s'affiche à l'étape « Mode de paiement ». Les
                modalités définitives sont confirmées avec l'équipe MTC après prise de contact.
              </p>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
